/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Page Tariffs And Services Reducer
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.24.0
 * @since 1.4.0
 */

import produce from 'immer';
import uniq from 'lodash/uniq';

import { LOGOUT_START_SUCCESSFUL } from '../PageAuth/constants/authConstants';
import {
    CLEAR_ROAMING_OPTIONS_SEARCH_ACTION,
    EProductsCart,
    EProductsCategory,
    EProductsPageFrom,
    EProductsPayment,
    EProductsTab,
    GET_ROAMING_OPTIONS_COUNTRIES_SEARCH,
    GET_ROAMING_OPTIONS_COUNTRIES_SEARCH_FAILURE,
    GET_ROAMING_OPTIONS_COUNTRIES_SEARCH_SUCCESS,
    GET_ROAMING_OPTIONS_FOR_COUNTRY,
    GET_ROAMING_OPTIONS_FOR_COUNTRY_FAILURE,
    GET_ROAMING_OPTIONS_FOR_COUNTRY_SUCCESS,
    GET_TARIFFS_AND_SERVICES_REGION_LIST_ACTION,
    GET_TARIFFS_AND_SERVICES_REGION_LIST_FAILURE,
    GET_TARIFFS_AND_SERVICES_REGION_LIST_SUCCESS,
    PRODUCTS_CLEAR_ACTION,
    PRODUCTS_GET_CATEGORY_LIST_ACTION,
    PRODUCTS_GET_CATEGORY_LIST_FAILURE,
    PRODUCTS_GET_CATEGORY_LIST_SUCCESS,
    PRODUCTS_GET_META_SUCCESS,
    PRODUCTS_GET_PRODUCT_LIST_ACTION,
    PRODUCTS_GET_PRODUCT_LIST_AVAILABLE_FAILURE_ACTION,
    PRODUCTS_GET_PRODUCT_LIST_CONNECTED_FAILURE_ACTION,
    PRODUCTS_GET_PRODUCT_LIST_SUCCESS,
    PRODUCTS_SET_CATEGORY_IDS,
    PRODUCTS_SET_CATEGORY_SELECTED,
    PRODUCTS_SET_FILTER_SELECTION,
    PRODUCTS_SET_PAGE_FROM,
    PRODUCTS_SET_TAB_SELECTED,
    PRODUCTS_START_WIZARD_FROM_SUBSCRIBER_ID,
    SET_INCLUDE_CAMPAIGN_SERVICE_PRODUCTS,
    SET_INCLUDE_CAMPAIGN_TARIFF_PRODUCTS,
    SET_IS_ROAMING_OPTIONS_ERROR,
    SET_SELECTED_COUNTRY_CODE,
    SET_SELECTED_ROAMING_OPTION,
    SET_TARIFFS_AND_SERVICES_FILTER_REGION_VISIBLE,
    SET_TARIFFS_AND_SERVICES_ITEM_SELECTED,
    SET_TARIFFS_AND_SERVICES_ITEMS_SELECTED,
    SET_TARIFFS_AND_SERVICES_PAYMENT_SELECTED,
    SET_TARIFFS_AND_SERVICES_REGION_DISABLED_ACTION,
    SET_TARIFFS_AND_SERVICES_REGION_SELECTED,
    SET_TARIFFS_AND_SERVICES_SET_TARIFF_RANGE,
    SET_TARIFFS_AND_SERVICES_TARIFF_SELECTED,
    TARIFFS_AND_SERVICES_SEARCH_START,
    TARIFFS_AND_SERVICES_SET_SEARCH_VALUE,
} from './tariffsAndServicesConstants';
import { IProductCardTariffsRangeSelected } from './tariffsAndServicesProductTariffTypes';
import { TProductItem } from './tariffsAndServicesProductTypes';
import {
    IProductCategoryIds,
    IProductOfferings,
    IProductsCategory,
    IProductsMeta,
    IProductsRegionItem,
    IProductsSelected,
    IRoamingCountry,
    IRoamingOption,
    TTariffsAndServicesActionTypes,
} from './tariffsAndServicesTypes';

export interface IProductOfferingsMap {
    [key: number]: IProductOfferings;
}

export interface IProductsState {
    cart: EProductsCart;
    categoryList: IProductsCategory[];
    categoryListIsError: boolean;
    categoryListIsLoading: boolean;
    categorySelected: number;

    clientId?: string;

    filterRegionVisible: boolean;
    filterShowSelectedOnly: boolean;
    itemsSelectedAvailable: IProductsSelected[];
    itemsSelectedConnected: IProductsSelected[];
    itemsSelectedTariffId: IProductsSelected;
    itemsSelectedTariffsRange: IProductCardTariffsRangeSelected;

    meta: IProductsMeta;
    pageFrom: EProductsPageFrom;
    paymentSelected: EProductsPayment;
    productListAvailable: TProductItem[];
    productListConnected: TProductItem[];

    regionList: IProductsRegionItem[];
    regionLoading: boolean;
    regionIsError: boolean;
    regionSelected: IProductsRegionItem;
    regionIsDisabled: boolean;
    searchValue: string;
    tabSelected: EProductsTab;

    productOfferingsAvailableIsError: boolean;
    productOfferingsConnectedIsError: boolean;
    errorText: string;
    productOfferingsIsLoading: boolean;
    productOfferingsMap: IProductOfferingsMap;
    productCategoryIds: IProductCategoryIds;

    roamingOptionsCountries: IRoamingCountry[];
    roamingOptionsCountriesIsLoading: boolean;
    isRoamingOptionsError: boolean;
    selectedCountryCode: string;

    roamingOptions: IRoamingOption[];
    roamingOptionsIsLoading: boolean;
    roamingOptionsSelected: IRoamingOption[];

    includeCampaignServiceProducts: boolean;
    includeCampaignTariffProducts: boolean;
}

export const initialTariffsAndServicesMeta: IProductsMeta = {
    campaignServicesCategoryId: 0,
    campaignTariffsCategoryId: 0,
    isCampaignInfoVisible: false,
    parentCategoryId: 1,
    popularCategoryId: 0,
    serviceMaxSelectIsSingle: false,
    servicesCategoryId: 0,
    servicesLimit: 0,
    tariffsCategoryId: 0,
    activeCategoryTariffId: 0,
    otherRatePlanCategoryId: 0,
    megaPowersCategoryId: 0,
    roamingCategoryId: 0,
};

export const initialTariffsAndServicesState: IProductsState = {
    cart: EProductsCart.PUBLIC,
    categorySelected: 0,
    categoryList: [],
    categoryListIsError: false,
    categoryListIsLoading: false,

    filterRegionVisible: false,
    filterShowSelectedOnly: false,
    itemsSelectedAvailable: [],
    itemsSelectedConnected: [],
    itemsSelectedTariffId: { id: 0 },
    itemsSelectedTariffsRange: {},

    errorText: '',
    meta: initialTariffsAndServicesMeta,
    pageFrom: EProductsPageFrom.HEADER,
    paymentSelected: EProductsPayment.ALL,
    productListAvailable: [],
    productListConnected: [],

    regionList: [],
    regionLoading: false,
    regionIsError: false,
    regionSelected: { label: '', value: 0 },
    regionIsDisabled: false,
    searchValue: '',
    tabSelected: EProductsTab.AVAILABLE,

    productOfferingsAvailableIsError: false,
    productOfferingsConnectedIsError: false,
    productOfferingsIsLoading: false,
    productOfferingsMap: {},
    productCategoryIds: {
        campaignTariffsCartegoryIds: [],
        tariffsCartegoryIds: [],
        campaignServicesCategoryIds: [],
        servicesCategoryIds: [],
    },

    roamingOptionsCountries: [],
    roamingOptionsCountriesIsLoading: false,
    isRoamingOptionsError: false,
    selectedCountryCode: '',

    roamingOptions: [],
    roamingOptionsIsLoading: false,
    roamingOptionsSelected: [],
    includeCampaignServiceProducts: false,
    includeCampaignTariffProducts: false,
};

export const tariffsAndServicesReducer = (
    state = initialTariffsAndServicesState,
    action: TTariffsAndServicesActionTypes,
) =>
    produce(state, (draft) => {
        switch (action.type) {
            case SET_INCLUDE_CAMPAIGN_SERVICE_PRODUCTS:
                draft.includeCampaignServiceProducts = action.payload.isIncluded;
                break;
            case SET_INCLUDE_CAMPAIGN_TARIFF_PRODUCTS:
                draft.includeCampaignTariffProducts = action.payload.isIncluded;
                break;

            case PRODUCTS_GET_META_SUCCESS:
                draft.meta = action.payload;
                break;

            case PRODUCTS_GET_CATEGORY_LIST_ACTION:
                draft.categoryListIsError = false;
                draft.categoryListIsLoading = true;
                break;
            case PRODUCTS_GET_CATEGORY_LIST_SUCCESS:
                draft.categoryList = action.payload;
                draft.categoryListIsLoading = false;
                break;
            case PRODUCTS_GET_CATEGORY_LIST_FAILURE:
                draft.categoryListIsError = true;
                draft.categoryListIsLoading = false;
                draft.errorText = action.payload;
                break;

            case PRODUCTS_GET_PRODUCT_LIST_ACTION:
                draft.productOfferingsAvailableIsError = false;
                draft.productOfferingsConnectedIsError = false;
                draft.productOfferingsIsLoading = true;
                break;
            case PRODUCTS_GET_PRODUCT_LIST_SUCCESS:
                draft.productOfferingsIsLoading = false;
                draft.productListAvailable = action.payload.productListAvailable;
                draft.productListConnected = action.payload.productListConnected;
                break;
            case PRODUCTS_GET_PRODUCT_LIST_AVAILABLE_FAILURE_ACTION:
                draft.productOfferingsAvailableIsError = true;
                draft.productOfferingsIsLoading = false;
                draft.errorText = action.payload;
                break;
            case PRODUCTS_GET_PRODUCT_LIST_CONNECTED_FAILURE_ACTION:
                draft.productOfferingsConnectedIsError = true;
                draft.productOfferingsIsLoading = false;
                draft.errorText = action.payload;
                break;

            case GET_TARIFFS_AND_SERVICES_REGION_LIST_ACTION:
                draft.regionLoading = true;
                draft.regionIsError = false;
                break;
            case GET_TARIFFS_AND_SERVICES_REGION_LIST_SUCCESS:
                draft.regionList = action.payload.data;
                draft.regionLoading = false;
                break;
            case GET_TARIFFS_AND_SERVICES_REGION_LIST_FAILURE:
                draft.regionIsError = true;
                draft.regionLoading = false;
                draft.errorText = action.payload;
                break;

            case SET_TARIFFS_AND_SERVICES_REGION_DISABLED_ACTION:
                draft.regionIsDisabled = action.payload.disabled;
                break;

            case TARIFFS_AND_SERVICES_SET_SEARCH_VALUE:
                draft.searchValue = action.payload;
                break;

            case TARIFFS_AND_SERVICES_SEARCH_START:
                if (state.searchValue) {
                    draft.categorySelected = EProductsCategory.SEARCH;
                }
                break;

            case PRODUCTS_SET_CATEGORY_SELECTED:
                draft.categorySelected = action.payload;
                draft.searchValue = '';
                break;

            case SET_TARIFFS_AND_SERVICES_FILTER_REGION_VISIBLE:
                draft.filterRegionVisible = action.payload.filterRegionVisible;
                break;

            case SET_TARIFFS_AND_SERVICES_PAYMENT_SELECTED:
                draft.paymentSelected = action.payload.paymentSelected;
                break;

            case SET_TARIFFS_AND_SERVICES_REGION_SELECTED:
                draft.regionSelected = action.payload.regionSelected;
                draft.itemsSelectedTariffId = { id: 0 };
                break;

            case PRODUCTS_SET_CATEGORY_IDS:
                draft.productCategoryIds = action.payload;
                break;

            case PRODUCTS_SET_PAGE_FROM:
                draft.clientId = action.payload.clientId;
                draft.pageFrom = action.payload.pageFrom;
                break;

            case SET_TARIFFS_AND_SERVICES_ITEM_SELECTED:
                let list: IProductsSelected[] = [];
                if (!draft.meta.serviceMaxSelectIsSingle) {
                    list = list.concat(
                        state.tabSelected === EProductsTab.CONNECTED
                            ? state.itemsSelectedConnected
                            : state.itemsSelectedAvailable,
                    );
                }
                let itemsSelected = [];

                if (
                    !list.find(
                        (item) =>
                            item.id === action.payload.id &&
                            item.personalOfferingId === action.payload.personalOfferingId,
                    )
                ) {
                    const { cart, ...item } = action.payload;
                    // Запрет на одновременный выбор НП с одинаковой основой
                    // При выборе второго такого НП выбор первого должен сбрасываться
                    list = list.filter((item) => item.id !== action.payload.id);
                    list.push(item);
                    itemsSelected = uniq(list);
                    if (cart) {
                        draft.cart = cart;
                    }
                } else {
                    itemsSelected = list.filter(
                        (item) =>
                            item.id !== action.payload.id ||
                            item.personalOfferingId !== action.payload.personalOfferingId,
                    );
                }
                if (state.tabSelected === EProductsTab.CONNECTED) {
                    draft.itemsSelectedConnected = itemsSelected;
                } else {
                    draft.itemsSelectedAvailable = itemsSelected;
                }

                break;

            case SET_TARIFFS_AND_SERVICES_ITEMS_SELECTED:
                if (action.payload.itemsSelectedAvailable) {
                    draft.itemsSelectedAvailable = action.payload.itemsSelectedAvailable;
                }
                if (action.payload.itemsSelectedConnected) {
                    draft.itemsSelectedConnected = action.payload.itemsSelectedConnected;
                }
                break;

            case SET_TARIFFS_AND_SERVICES_TARIFF_SELECTED:
                const { cart, ...item } = action.payload;
                draft.itemsSelectedTariffId =
                    draft.itemsSelectedTariffId.id !== item.id ||
                    (draft.itemsSelectedTariffId.personalOfferingId &&
                        draft.itemsSelectedTariffId.personalOfferingId !== item.personalOfferingId)
                        ? item
                        : { id: 0 };
                if (cart) {
                    draft.cart = cart;
                }
                break;
            case SET_TARIFFS_AND_SERVICES_SET_TARIFF_RANGE:
                draft.itemsSelectedTariffsRange[action.payload.id] = action.payload.rangeId;
                break;

            case PRODUCTS_SET_TAB_SELECTED:
                draft.tabSelected = action.payload.tabSelected;
                break;

            case PRODUCTS_SET_FILTER_SELECTION:
                draft.filterShowSelectedOnly = action.payload;
                break;

            case PRODUCTS_START_WIZARD_FROM_SUBSCRIBER_ID:
                draft.tabSelected = EProductsTab.CONNECTED;
                draft.categorySelected = EProductsCategory.SERVICES;
                draft.regionSelected = { value: action.payload.regionId, label: action.payload.regionId.toString() };
                draft.itemsSelectedConnected = [{ id: action.payload.id }];
                break;

            case GET_ROAMING_OPTIONS_COUNTRIES_SEARCH:
                draft.roamingOptionsCountries = [];
                draft.roamingOptionsCountriesIsLoading = true;
                break;
            case GET_ROAMING_OPTIONS_COUNTRIES_SEARCH_SUCCESS:
                draft.roamingOptionsCountries = action.payload.countries;
                draft.roamingOptionsCountriesIsLoading = false;
                break;
            case GET_ROAMING_OPTIONS_COUNTRIES_SEARCH_FAILURE:
                draft.roamingOptionsCountries = [];
                draft.errorText = action.payload;
                draft.roamingOptionsCountriesIsLoading = false;
                break;

            case SET_IS_ROAMING_OPTIONS_ERROR:
                draft.isRoamingOptionsError = action.payload.isRoamingOptionsError;
                break;

            case SET_SELECTED_COUNTRY_CODE:
                draft.selectedCountryCode = action.payload.selectedCountryCode;
                break;

            case SET_SELECTED_ROAMING_OPTION:
                const selectedRoamingOptions: IRoamingOption[] = state.roamingOptionsSelected;
                const { ...roamingOption } = action.payload;

                if (selectedRoamingOptions.find(selectedOption => selectedOption.id === action.payload.id)) {
                    draft.roamingOptionsSelected =
                        selectedRoamingOptions.filter(selectedOption => selectedOption.id !== action.payload.id);
                } else {
                    draft.roamingOptionsSelected = [...selectedRoamingOptions, roamingOption];
                }

                break;

            case GET_ROAMING_OPTIONS_FOR_COUNTRY:
                draft.roamingOptions = [];
                draft.roamingOptionsIsLoading = true;
                break;
            case GET_ROAMING_OPTIONS_FOR_COUNTRY_SUCCESS:
                draft.roamingOptions = action.payload.roamingOptions;
                draft.roamingOptionsIsLoading = false;
                break;
            case GET_ROAMING_OPTIONS_FOR_COUNTRY_FAILURE:
                draft.roamingOptions = [];
                draft.roamingOptionsIsLoading = false;
                draft.errorText = action.payload;
                break;

            case CLEAR_ROAMING_OPTIONS_SEARCH_ACTION:
                //draft.roamingOptions = [];
                draft.roamingOptionsCountries = [];
                draft.selectedCountryCode = '';
                break;

            case PRODUCTS_CLEAR_ACTION:
            case LOGOUT_START_SUCCESSFUL:
                return initialTariffsAndServicesState;

            default:
                break;
        }
    });
